import type { LayoutElement } from '../types';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';
import { equipmentProcessTypeLabel } from '../features/equipment/equipmentConstants';
import { isDxfEquipmentLayoutElement } from './layout3d';

export type SimulationToolOption = {
  id: string;
  name: string;
  processType?: string;
};

function isLayoutToolElement(element: LayoutElement): boolean {
  if (!element.metadata?.visible) return false;
  if (element.type === 'stocker' || element.type === 'oht-vehicle') return false;
  if (isDxfEquipmentLayoutElement(element)) return true;
  return element.type === 'ohb' || element.type === 'lifter' || element.type === 'stb';
}

function isToolEquipmentInstance(instance: EquipmentInstance): boolean {
  return instance.objectType !== 'stocker' && instance.objectType !== 'room';
}

/**
 * Process tools the live stream can occupy — equipment overlays plus layout
 * floor tools, then any extra stream `toolId`s that are not on the canvas.
 */
export function listSimulationTools(
  instances: readonly EquipmentInstance[],
  elements: readonly LayoutElement[],
  extraToolIds: readonly string[] = []
): SimulationToolOption[] {
  const byId = new Map<string, SimulationToolOption>();
  const names = new Set<string>();

  for (const instance of instances) {
    if (!isToolEquipmentInstance(instance)) continue;
    const name = instance.name?.trim() || instance.id;
    byId.set(instance.id, {
      id: instance.id,
      name,
      processType: equipmentProcessTypeLabel(instance.equipmentType),
    });
    names.add(name);
  }

  const overlayIds = new Set(byId.keys());
  for (const element of elements) {
    if (!isLayoutToolElement(element)) continue;
    const dxfId = element.customProperties?.dxfEquipmentId;
    if (typeof dxfId === 'string' && overlayIds.has(dxfId)) continue;
    if (overlayIds.has(element.id)) continue;
    const name = element.metadata.name?.trim() || element.id;
    const props = element.customProperties || {};
    byId.set(element.id, {
      id: element.id,
      name,
      processType: equipmentProcessTypeLabel(
        props.processType ?? props.equipmentType ?? element.metadata.name
      ),
    });
    names.add(name);
  }

  for (const toolId of extraToolIds) {
    const id = toolId.trim();
    if (!id || byId.has(id) || names.has(id)) continue;
    byId.set(id, { id, name: id });
  }

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
